import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';

export const DISCARD_MESSAGE = '작성 중인 변경사항이 저장되지 않았습니다. 내용을 버리고 나갈까요?';
const DraftsContext = createContext(null);

function NavigationGuard({ drafts }) {
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (
        currentLocation.pathname === nextLocation.pathname &&
        currentLocation.search === nextLocation.search
      )
        return false;
      return [...drafts.current.values()].some(
        (draft) => draft.current.dirty || draft.current.busy,
      );
    },
    [drafts],
  );
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if ([...drafts.current.values()].some((draft) => draft.current.busy)) {
      window.alert('처리 중입니다. 완료 후 이동해주세요.');
      blocker.reset();
    } else if (window.confirm(DISCARD_MESSAGE)) {
      blocker.proceed();
    } else blocker.reset();
  }, [blocker, drafts]);
  return null;
}

export function AdminDraftProvider({ children }) {
  const drafts = useRef(new Map());
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return (
    <DraftsContext.Provider value={drafts}>
      {dataRouter && <NavigationGuard drafts={drafts} />}
      {children}
    </DraftsContext.Provider>
  );
}

export function draftSnapshot(value) {
  return JSON.stringify(value, (_key, item) => {
    if (typeof File !== 'undefined' && item instanceof File)
      return [item.name, item.size, item.type, item.lastModified];
    if (typeof item === 'string' && /^(?:\s|<p><br\s*\/?><\/p>)*$/i.test(item)) return '';
    if (item && typeof item === 'object' && !Array.isArray(item))
      return Object.fromEntries(
        Object.keys(item)
          .sort()
          .map((key) => [key, item[key]]),
      );
    return item;
  });
}

// Capture the loaded/opened form, not asynchronous preview/search state. Call
// markClean only after successful persistence; failed saves must retain drafts.
export default function useAdminDraftGuard(value, { enabled = true, busy = false } = {}) {
  const registry = useContext(DraftsContext);
  const baseline = useRef(undefined);
  const state = useRef({ dirty: false, busy: false });
  const snapshot = draftSnapshot(value);
  if (!enabled) baseline.current = undefined;
  else if (baseline.current === undefined) baseline.current = snapshot;
  state.current = { dirty: enabled && snapshot !== baseline.current, busy: enabled && busy };
  useLayoutEffect(() => {
    const entries = registry?.current;
    entries?.set(state, state);
    return () => entries?.delete(state);
  }, [registry]);
  useEffect(() => {
    const beforeUnload = (event) => {
      if (!state.current.dirty && !state.current.busy) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);
  return {
    dirty: state.current.dirty,
    resetBaseline: () => {
      baseline.current = undefined;
    },
    confirmDiscard: () =>
      !state.current.busy && (!state.current.dirty || window.confirm(DISCARD_MESSAGE)),
    markClean: (next = value) => {
      baseline.current = draftSnapshot(next);
      state.current = { dirty: false, busy: false };
    },
  };
}
